import React from 'react';

import type { DialogProps } from '@mui/material';
import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material';
import type { NavigateOptions, To } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { createScopedI18n } from '../i18n/i18n';

export type NavigateToDialogProps = {
  title?: React.ReactNode;
  description?: React.ReactNode;
  to?: To;
  navigationOptions?: NavigateOptions;
} & DialogProps;

const dialogI18n = createScopedI18n('components.navigate_to_dialog');

const NavigateToDialog = ({
  title,
  description,
  to,
  navigationOptions,
  open,
  onClose,
  ...dialogProps
}: NavigateToDialogProps) => {
  const navigate = useNavigate();

  const onCloseHandler = React.useCallback(() => {
    onClose?.({}, 'escapeKeyDown');
  }, [onClose]);

  const navigateToHandler = React.useCallback(() => {
    onCloseHandler();
    if (to) {
      navigate(to, navigationOptions);
    }
  }, [navigate, onCloseHandler, to]);

  return (
    <Dialog open={open} onClose={onClose} {...dialogProps}>
      {title ? <DialogTitle>{title}</DialogTitle> : null}

      {description ? (
        <DialogContent>
          <DialogContentText>{description}</DialogContentText>
        </DialogContent>
      ) : null}

      <DialogActions>
        <Button onClick={onCloseHandler}>{dialogI18n('close')}</Button>
        <Button onClick={navigateToHandler}>{dialogI18n('ok')}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default NavigateToDialog;
